import { StrToBool } from './serialize-graph-types';
import {
  SceneGraphNode,
  HumanObjectTransform,
  HumanObjectRenderable,
  EuclideanData,
} from '../../models/scene-graph-node';

const EPS = 1e-4;

export namespace SerializeGraphUtils {
  function filterEuclidean(data: EuclideanData, target: number = 0) {
    ['x', 'y', 'z'].forEach((dimension) => {
      if (data[dimension] === undefined) {
        return;
      }

      if (Math.abs(data[dimension] - target) < EPS) {
        delete data[dimension];
      }
    });
  }

  export function filterTransformProperties(transform: HumanObjectTransform) {
    ['translate', 'rotate', 'pivot'].forEach((property) => {
      if (transform[property] === undefined) {
        return;
      }

      filterEuclidean(transform[property]);
      if (Object.keys(transform[property]).length === 0) {
        delete transform[property];
      }
    });

    if (transform.scale === undefined) {
      return;
    }

    filterEuclidean(transform.scale, 1.0);
    if (Object.keys(transform.scale).length === 0) {
      delete transform.scale;
    }
  }

  // serialize non-default values on human object properties
  export function serializeObjectProperties(humanObject: any, node: SceneGraphNode): void {
    ['backfaces', 'displayName', 'layer'].forEach((property) => {
      if (humanObject[property]) {
        node[property] = humanObject[property];
      }
    });
  }

  export function serializeTransformData(humanObject: any, node: SceneGraphNode): void {
    const transform = new HumanObjectTransform();
    transform.translate = JSON.parse(JSON.stringify(humanObject.translate));
    transform.rotate = JSON.parse(JSON.stringify(humanObject.rotate));
    transform.scale = JSON.parse(JSON.stringify(humanObject.scale));
    transform.pivot = JSON.parse(JSON.stringify(humanObject.pivot));
    filterTransformProperties(transform);

    Object.entries(transform).forEach(([transformType, transformVal]) => {
      node[transformType] = JSON.parse(JSON.stringify(transformVal));
    });
    if (node.transformId) {
      delete node.transformId;
    }
  }

  export function serializeRenderables(humanObject: any, node: SceneGraphNode): SceneGraphNode {
    const assetTypes: string[] = ['geometryId', 'morphId', 'materialId'];
    const renderables: HumanObjectRenderable[] = [];

    if (humanObject.renderables) {
      humanObject.renderables.forEach((renderTarget) => {
        const renderable = new HumanObjectRenderable();

        // attach object's renderables to the graph
        assetTypes.forEach((assetType) => {
          if (renderTarget[assetType]) {
            renderable[assetType] = renderTarget[assetType];
          }
        });

        // TODO is this supposed to filter out layer's with 0?
        if (renderTarget.layer) {
          renderable.layer = renderTarget.layer;
        }

        // add
        renderables.push(renderable);
      });
    }

    // serialize on to scene graph node
    if (renderables.length === 1) {
      node = { ...node, ...renderables[0] };
    } else if (renderables.length > 1) {
      node.renderables = renderables;
    }

    return node;
  }

  export function serializeHumanObject(humanObject: any, excludeMap: StrToBool): SceneGraphNode {
    if (excludeMap[humanObject.objectId]) {
      return null;
    }
    let result: SceneGraphNode = new SceneGraphNode();
    result.objectId = humanObject.objectId;

    // id
    // By default object IDs do not change.
    // The only time they would change is when making a deep copy of an entire model in scene editor.

    // properties
    serializeObjectProperties(humanObject, result);

    // if there's a transform Id, prefer it over transform data
    // to maintain backwards compatability, fall back to raw transform data
    if (humanObject.transformId) {
      result.transformId = humanObject.transformId;
    } else {
      serializeTransformData(humanObject, result);
    }

    // object renderables
    result = serializeRenderables(humanObject, result);

    // downward traversal
    if (humanObject.objects && humanObject.objects.length) {
      result.objects = [];
    }
    humanObject.objects.forEach((childObject) => {
      const childNode = serializeHumanObject(childObject, excludeMap);
      if (childNode !== null) {
        result.objects.push(childNode);
      }
    }, this);

    return result;
  }
}
